import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Mail, Bell, Settings, Save, Loader2, Eye, AlertCircle,
  Variable, Lock, Power, Code, Wand2,
} from 'lucide-react';

import { Button, Card, Badge } from '../../ui-v2';
import { editarPlantillaNotif } from '../api';
import {
  renderTemplate, VARIABLES_POR_TIPO, VARS_EJEMPLO, DESCRIPCION_VARIABLE,
} from './renderTemplate';
import EditorRico from './EditorRico';
import { quillToEmailHtml } from './quillToEmailHtml';

/**
 * EditorPlantilla v2 — añade modo WYSIWYG en el tab Email.
 *
 * El tab Email tiene un toggle "Visual / HTML":
 *   - Visual (por defecto): editor rico Quill con toolbar limitado a
 *     opciones que sobreviven en clientes de email.
 *   - HTML: textarea con HTML crudo, para edición avanzada.
 *
 * Al cambiar de Visual a HTML, el HTML se pasa por quillToEmailHtml para
 * convertir clases ql-* a estilos inline. El tab In-app sigue texto plano.
 */

const TABS = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'inapp', label: 'In-app', icon: Bell },
  { id: 'config', label: 'Configuración', icon: Settings },
];

export default function EditorPlantilla({ plantilla, onGuardado }) {
  const [tab, setTab] = useState('email');
  const [draft, setDraft] = useState(() => clonar(plantilla));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(false);

  useEffect(() => {
    setDraft(clonar(plantilla));
    setError(null); setExito(false);
    setTab('email');
  }, [plantilla?.id]);

  const dirty = useMemo(() => {
    if (!plantilla || !draft) return false;
    const claves = [
      'descripcion',
      'email_activado_default', 'email_asunto', 'email_cuerpo_html',
      'inapp_activado_default', 'inapp_titulo', 'inapp_cuerpo',
      'activa', 'usuario_puede_silenciar',
    ];
    return claves.some((k) => normaliza(plantilla[k]) !== normaliza(draft[k]));
  }, [plantilla, draft]);

  /* EL VOCABULARIO: el del SERVIDOR manda, y el mapa local es el respaldo.
   *
   * Habia DOS mapas sin solaparse: `VARIABLES_POR_TIPO` aqui con 12 tipos de intervenciones, y
   * `validarPlantilla.VARIABLES` en el servidor con los de incorporaciones. Dos mapas que no se
   * solapan **no se contradicen: se complementan mal**. Nunca dan respuestas distintas a la misma
   * pregunta — dan silencio a preguntas distintas, y eso no se nota.
   *
   * Ahora el `GET` manda `vocabulario` por plantilla. Se prefiere ese cuando viene declarado; si no,
   * el mapa local, que ademas es el unico que tiene `DESCRIPCION_VARIABLE`. Y si no hay ninguno, se
   * DICE que no se sabe en vez de afirmar que no tiene. */
  const vocab = (() => {
    const v = plantilla?.vocabulario;
    if (v && v.declarado) {
      return { declarado: true, datos: v.datos || [], bloques: v.bloques || [], usadas: v.usadas || [] };
    }
    const local = VARIABLES_POR_TIPO[draft.tipo];
    if (local && local.length) {
      return {
        declarado: true,
        datos: local.filter((x) => !x.endsWith('_html')),
        bloques: local.filter((x) => x.endsWith('_html')),
        usadas: (v && v.usadas) || [],
      };
    }
    return { declarado: false, datos: [], bloques: [], usadas: (v && v.usadas) || [] };
  })();

  function actualizarCampo(campo, valor) {
    setDraft((d) => ({ ...d, [campo]: valor }));
    setError(null); setExito(false);
  }

  async function guardar() {
    setSaving(true); setError(null); setExito(false);
    try {
      const payload = {
        descripcion: draft.descripcion,
        email_activado_default: !!draft.email_activado_default,
        email_asunto: draft.email_asunto,
        email_cuerpo_html: draft.email_cuerpo_html,
        inapp_activado_default: !!draft.inapp_activado_default,
        inapp_titulo: draft.inapp_titulo,
        inapp_cuerpo: draft.inapp_cuerpo,
        activa: !!draft.activa,
        usuario_puede_silenciar: !!draft.usuario_puede_silenciar,
      };
      await editarPlantillaNotif(draft.id, payload);
      setExito(true);
      onGuardado?.({ ...plantilla, ...payload });
      setTimeout(() => setExito(false), 2500);
    } catch (e) {
      console.error('[EditorPlantilla] guardar', e);
      setError(e?.response?.data?.error || 'No se pudo guardar la plantilla.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                {draft.tipo}
              </code>
              {!draft.activa && <Badge variant="secondary">Inactiva</Badge>}
              {!draft.usuario_puede_silenciar && (
                <Badge variant="warning">
                  <Lock className="w-3 h-3" />
                  Obligatoria
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {draft.descripcion || <em className="text-slate-400">Sin descripción</em>}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {exito && (
              <span className="text-xs text-emerald-600 font-medium">✓ Guardada</span>
            )}
            <Button size="sm" onClick={guardar} disabled={!dirty || saving}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Guardar cambios
            </Button>
          </div>
        </div>

        <div className="flex gap-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={
                'px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ' +
                (tab === id
                  ? 'bg-primary/10 text-primary'
                  : 'text-slate-600 hover:bg-slate-100')
              }
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {error}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-hidden">
        {tab === 'email' && (
          <EmailTab draft={draft} vocab={vocab} actualizarCampo={actualizarCampo} />
        )}
        {tab === 'inapp' && (
          <InAppTab draft={draft} vocab={vocab} actualizarCampo={actualizarCampo} />
        )}
        {tab === 'config' && (
          <ConfigTab draft={draft} actualizarCampo={actualizarCampo} />
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
//  Tab: Email — con modo Visual/HTML
// ----------------------------------------------------------------

function EmailTab({ draft, vocab, actualizarCampo }) {
  const [modoEdicion, setModoEdicion] = useState('visual'); // 'visual' | 'html'
  const asuntoRef = useRef(null);
  const richRef = useRef(null);
  const htmlRef = useRef(null);
  const [refActivoAsunto, setRefActivoAsunto] = useState(false);

  const previewAsunto = renderTemplate(draft.email_asunto || '', VARS_EJEMPLO);
  const previewCuerpo = renderTemplate(draft.email_cuerpo_html || '', VARS_EJEMPLO);

  // Inserción de variables: en asunto va siempre al input.
  // En cuerpo, depende del modo activo (Quill o textarea).
  function insertarVariable(varName) {
    const tag = `{{${varName}}}`;
    if (refActivoAsunto) {
      insertarEnInput(asuntoRef, tag, (valor) => actualizarCampo('email_asunto', valor));
      return;
    }
    if (modoEdicion === 'visual') {
      richRef.current?.insertarEnCursor(tag);
    } else {
      insertarEnInput(htmlRef, tag, (valor) => actualizarCampo('email_cuerpo_html', valor));
    }
  }

  // Al pasar de Visual a HTML: limpiar el HTML primero
  function cambiarModo(nuevoModo) {
    if (modoEdicion === nuevoModo) return;
    if (modoEdicion === 'visual' && nuevoModo === 'html') {
      // El HTML que Quill ha producido va al textarea, limpio
      const limpio = quillToEmailHtml(draft.email_cuerpo_html || '');
      actualizarCampo('email_cuerpo_html', limpio);
    }
    setModoEdicion(nuevoModo);
  }

  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
      {/* Editor */}
      <div className="p-5 overflow-y-auto">
        <ToggleConChip
          label="Email activado por defecto"
          hint="Si el usuario no toca sus preferencias, ¿se le envía este email?"
          checked={!!draft.email_activado_default}
          onChange={(v) => actualizarCampo('email_activado_default', v)}
        />

        <div className="mt-5">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">
            Asunto
          </label>
          <input
            ref={asuntoRef}
            type="text"
            value={draft.email_asunto || ''}
            onChange={(e) => actualizarCampo('email_asunto', e.target.value)}
            onFocus={() => setRefActivoAsunto(true)}
            onBlur={() => setRefActivoAsunto(false)}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
            placeholder="Asunto del email…"
          />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-slate-700">
              Cuerpo
            </label>
            <div className="flex gap-1 text-[10px]">
              <button
                type="button"
                onClick={() => cambiarModo('visual')}
                className={
                  'px-2 py-1 rounded-md flex items-center gap-1 transition-colors ' +
                  (modoEdicion === 'visual'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-slate-500 hover:bg-slate-100')
                }
              >
                <Wand2 className="w-3 h-3" />
                Visual
              </button>
              <button
                type="button"
                onClick={() => cambiarModo('html')}
                className={
                  'px-2 py-1 rounded-md flex items-center gap-1 transition-colors ' +
                  (modoEdicion === 'html'
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-slate-500 hover:bg-slate-100')
                }
              >
                <Code className="w-3 h-3" />
                HTML
              </button>
            </div>
          </div>

          {modoEdicion === 'visual' ? (
            <EditorRico
              ref={richRef}
              value={draft.email_cuerpo_html || ''}
              onChange={(html) => actualizarCampo('email_cuerpo_html', html)}
              placeholder="Escribe el cuerpo del email…"
              minHeight={320}
            />
          ) : (
            <textarea
              ref={htmlRef}
              value={draft.email_cuerpo_html || ''}
              onChange={(e) => actualizarCampo('email_cuerpo_html', e.target.value)}
              rows={16}
              spellCheck={false}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs font-mono leading-relaxed"
              placeholder="<html>...</html>"
            />
          )}

          <p className="text-[10px] text-slate-400 mt-1">
            {modoEdicion === 'visual'
              ? 'Edición visual con formato. Al cambiar a HTML, las clases CSS se convierten a estilos inline (email-safe).'
              : 'HTML crudo. Usa estilos inline (style="…") — los clientes de email ignoran clases y <style>.'}
          </p>
        </div>

        <ListaVariables vocab={vocab} onInsertar={insertarVariable} />
      </div>

      {/* Preview */}
      <div className="p-5 overflow-y-auto bg-slate-50">
        <div className="flex items-center gap-1.5 mb-3 text-xs text-slate-500">
          <Eye className="w-3.5 h-3.5" />
          Previsualización con valores de ejemplo
        </div>

        <Card className="overflow-hidden p-0">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Asunto</div>
            <div className="text-sm text-slate-900 font-medium mt-0.5">
              {previewAsunto || <span className="text-slate-300 italic">vacío</span>}
            </div>
          </div>
          <div className="bg-white">
            {previewCuerpo ? (
              <iframe
                title="Preview email"
                srcDoc={previewCuerpo}
                sandbox=""
                className="w-full"
                style={{ height: 500, border: 'none' }}
              />
            ) : (
              <div className="p-8 text-center text-sm text-slate-300 italic">vacío</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
//  Tab: In-app (sin cambios — texto plano)
// ----------------------------------------------------------------

function InAppTab({ draft, vocab, actualizarCampo }) {
  const tituloRef = useRef(null);
  const cuerpoRef = useRef(null);
  const [refActivo, setRefActivo] = useState(null);

  const previewTitulo = renderTemplate(draft.inapp_titulo || '', VARS_EJEMPLO);
  const previewCuerpo = renderTemplate(draft.inapp_cuerpo || '', VARS_EJEMPLO);

  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
      <div className="p-5 overflow-y-auto">
        <ToggleConChip
          label="In-app activado por defecto"
          hint="Si el usuario no toca sus preferencias, ¿se le muestra en la campanita?"
          checked={!!draft.inapp_activado_default}
          onChange={(v) => actualizarCampo('inapp_activado_default', v)}
        />

        <div className="mt-5">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">Título</label>
          <input
            ref={tituloRef}
            type="text"
            value={draft.inapp_titulo || ''}
            onChange={(e) => actualizarCampo('inapp_titulo', e.target.value)}
            onFocus={() => setRefActivo(tituloRef)}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
            placeholder="Título corto…"
          />
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-slate-700 mb-1.5">Cuerpo</label>
          <textarea
            ref={cuerpoRef}
            value={draft.inapp_cuerpo || ''}
            onChange={(e) => actualizarCampo('inapp_cuerpo', e.target.value)}
            onFocus={() => setRefActivo(cuerpoRef)}
            rows={5}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
            placeholder="Mensaje breve para la campanita…"
          />
          <p className="text-[10px] text-slate-400 mt-1">
            Texto plano (no HTML). El popover lo muestra truncado a ~2 líneas.
          </p>
        </div>

        <ListaVariables
          vocab={vocab}
          onInsertar={(varName) => {
            const ref = refActivo || cuerpoRef;
            insertarEnInput(ref, `{{${varName}}}`, (valor) => {
              actualizarCampo(ref === tituloRef ? 'inapp_titulo' : 'inapp_cuerpo', valor);
            });
          }}
        />
      </div>

      <div className="p-5 overflow-y-auto bg-slate-50">
        <div className="flex items-center gap-1.5 mb-3 text-xs text-slate-500">
          <Eye className="w-3.5 h-3.5" />
          Previsualización (estilo campanita)
        </div>

        <Card className="overflow-hidden p-0">
          <div className="px-4 py-3 bg-primary/5">
            <div className="flex items-start gap-3">
              <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">
                  {previewTitulo || <span className="text-slate-300 italic">título vacío</span>}
                </div>
                {previewCuerpo && (
                  <div className="text-xs text-slate-600 mt-0.5">{previewCuerpo}</div>
                )}
                <div className="text-[10px] text-slate-400 mt-1">hace un momento</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ConfigTab({ draft, actualizarCampo }) {
  return (
    <div className="p-5 overflow-y-auto max-w-2xl">
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1.5">
          Descripción interna
        </label>
        <textarea
          value={draft.descripcion || ''}
          onChange={(e) => actualizarCampo('descripcion', e.target.value)}
          rows={2}
          maxLength={255}
          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
          placeholder="Texto humano para que admins entiendan qué evento dispara esta plantilla."
        />
        <p className="text-[10px] text-slate-400 mt-1">
          Solo visible en la admin. No se envía al usuario.
        </p>
      </div>

      <div className="mt-6 pt-6 border-t border-slate-100 space-y-5">
        <ToggleConChip
          label="Plantilla activa"
          hint="Si la desactivas, este tipo de notificación deja de enviarse a cualquiera (ni email ni in-app)."
          icon={Power}
          checked={!!draft.activa}
          onChange={(v) => actualizarCampo('activa', v)}
        />

        <ToggleConChip
          label="El usuario puede silenciarla"
          hint="Si la desactivas, esta notificación es OBLIGATORIA y los usuarios no podrán deshabilitarla en sus preferencias. Úsalo para confirmaciones críticas."
          icon={Lock}
          checked={!!draft.usuario_puede_silenciar}
          onChange={(v) => actualizarCampo('usuario_puede_silenciar', v)}
        />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
//  Helpers
// ----------------------------------------------------------------

function ListaVariables({ vocab, onInsertar }) {
  const chip = (v) => (
    <button
      key={v}
      type="button"
      onClick={() => onInsertar(v)}
      title={DESCRIPCION_VARIABLE[v] || v}
      className="text-[11px] font-mono px-2 py-1 rounded border border-slate-200 bg-white hover:bg-primary-50 hover:border-primary-300"
    >
      {`{{${v}}}`}
    </button>
  );

  /* SIN VOCABULARIO DECLARADO NO SE MIENTE.
   *
   * Antes decia «Este tipo no define variables especificas», que afirma algo que no sabemos: para
   * las 21 plantillas de intervenciones y soporte el vocabulario vive en el contexto que arma cada
   * emisor y nadie lo ha declarado.
   *
   * Se enseñan las que USA AHORA, etiquetadas como lo que son. **No es el catalogo**: son lo que
   * hay escrito hoy. Si alguien borra una, desaparece de aqui — por eso no se presenta como «las
   * disponibles», que seria un dato que empeora con el uso. */
  if (!vocab.declarado) {
    return (
      <div className="mt-5">
        <div className="flex items-center gap-1.5 text-[11px] text-amber-700 mb-1.5 uppercase tracking-wider">
          <Variable className="w-3 h-3" />
          Vocabulario no declarado
        </div>
        <p className="text-[11px] text-slate-500 mb-2">
          Para esta plantilla no está declarado qué variables admite, así que no hay catálogo que
          ofrecer. Estas son <strong>las que usa ahora</strong> — sirven de referencia, no de lista
          completa.
        </p>
        {vocab.usadas.length === 0 ? (
          <p className="text-[11px] text-slate-400 italic">Ahora mismo no usa ninguna.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">{vocab.usadas.map(chip)}</div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-2 uppercase tracking-wider">
        <Variable className="w-3 h-3" />
        Variables disponibles · click para insertar
      </div>

      {/* DOS CLASES, y la diferencia importa al escribir: un dato se coloca donde se quiera; un
          bloque ya viene montado y solo se pone o se quita. El sufijo `_html` es el mismo que el
          servidor usa para decidir que se escapa, asi que la separacion no se inventa aqui. */}
      {vocab.datos.length > 0 && (
        <>
          <p className="text-[11px] text-slate-500 mb-1">
            <strong>Datos</strong> — se colocan donde quieras
          </p>
          <div className="flex flex-wrap gap-1.5 mb-3">{vocab.datos.map(chip)}</div>
        </>
      )}

      {vocab.bloques.length > 0 && (
        <>
          <p className="text-[11px] text-slate-500 mb-1">
            <strong>Bloques</strong> — estructura ya montada: solo colocar o quitar, no se pueden
            reordenar por dentro
          </p>
          <div className="flex flex-wrap gap-1.5">{vocab.bloques.map(chip)}</div>
        </>
      )}
    </div>
  );
}

function ToggleConChip({ label, hint, icon: Icon, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-slate-300 text-primary"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm text-slate-900 font-medium">
          {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
          {label}
        </div>
        {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
    </label>
  );
}

function clonar(p) { return p ? { ...p } : null; }
function normaliza(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? '1' : '0';
  return String(v);
}

function insertarEnInput(ref, texto, setter) {
  const el = ref.current;
  if (!el) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const nuevo = el.value.slice(0, start) + texto + el.value.slice(end);
  setter(nuevo);
  setTimeout(() => {
    el.focus();
    const pos = start + texto.length;
    el.setSelectionRange(pos, pos);
  }, 0);
}
